import React, { useState } from 'react';
import { HeartPulse } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Classification indicative inspiree des recommandations europeennes (ESC/ESH)
const classify = (sys, dia, t) => {
  if (sys < 90 || dia < 60) return { label: t('bp_cat_hypo'), color: '#3B82F6' };
  if (sys < 120 && dia < 80) return { label: t('bp_cat_optimal'), color: '#10B981' };
  if (sys < 130 && dia < 85) return { label: t('bp_cat_normal'), color: '#10B981' };
  if (sys < 140 && dia < 90) return { label: t('bp_cat_high_normal'), color: '#F59E0B' };
  if (sys < 160 && dia < 100) return { label: t('bp_cat_grade1'), color: '#F59E0B' };
  if (sys < 180 && dia < 110) return { label: t('bp_cat_grade2'), color: '#EF4444' };
  return { label: t('bp_cat_grade3'), color: '#EF4444' };
};

const BloodPressureChecker = () => {
  const { t } = useLanguage();
  const [sys, setSys] = useState('');
  const [dia, setDia] = useState('');

  const sysNum = parseInt(sys, 10);
  const diaNum = parseInt(dia, 10);
  const valid = sysNum > 0 && diaNum > 0;
  const cat = valid ? classify(sysNum, diaNum, t) : null;
  const critical = valid && (sysNum >= 180 || diaNum >= 110);

  return (
    <ToolPageLayout
      title={t('bp_title')}
      subtitle={t('bp_subtitle')}
      specialty="cardiologue"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <HeartPulse className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('bp_info')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('bp_sys_label')}</Label>
          <Input
            type="number"
            min="40"
            max="260"
            value={sys}
            onChange={(e) => setSys(e.target.value)}
            placeholder={t('bp_sys_placeholder')}
            className="rounded-xl"
          />
        </div>
        <div>
          <Label className="mb-1 block">{t('bp_dia_label')}</Label>
          <Input
            type="number"
            min="30"
            max="160"
            value={dia}
            onChange={(e) => setDia(e.target.value)}
            placeholder={t('bp_dia_placeholder')}
            className="rounded-xl"
          />
        </div>
      </div>

      {cat && (
        <div
          className="rounded-2xl p-6 text-center border"
          style={{ borderColor: `${cat.color}40`, backgroundColor: `${cat.color}0D` }}
        >
          <div className="text-sm text-[#64748B] mb-1">{sysNum}/{diaNum} mmHg</div>
          <div className="text-2xl font-bold" style={{ color: cat.color }}>{cat.label}</div>
        </div>
      )}

      {critical && (
        <p className="mt-4 text-sm font-medium text-[#EF4444]">
          {t('bp_critical_warning')}
        </p>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('bp_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default BloodPressureChecker;
